import React, { useCallback, useMemo } from 'react';

import classnames from 'classnames';
import Head from 'next/head';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';

import InfiniteListPage, { PageLoading } from 'components/Globals/Layout/InfiniteListPage';
import useQuickView, { QUICK_VIEW_TYPES } from 'components/Globals/Layout/QuickView';
import NoResult from 'components/Globals/NoResult';
import CalendarProductionListing, { CalendarViewSlug } from 'components/Productions/Display/CalendarProductionListing';
import ProductionPublicSlug from 'components/Productions/Display/Slugs/ProductionPublicSlug';

import { TP } from 'constants/index';

import usePageContext from 'utils/hooks/usePageContext';
import { groupProductionsPerformances, filterValidProductionsEvents } from 'utils/productions';
import ProductionSEO from 'utils/seo/production';

import { useTranslation } from 'src/i18n';

import { VIEW_MODES } from 'operabase-router/constants';

import classes from './ProductionListFeed.module.scss';

const JsonLD = ({ events }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const filteredEvents = useMemo(() => filterValidProductionsEvents(events), [events]);

  if (!filteredEvents?.length) {
    return null;
  }

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: filteredEvents?.map((production, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: ProductionSEO.getEventJsonLd({ t, entity: production }),
    })),
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(itemListSchema),
        }}
      />
    </Head>
  );
};

const RenderSlugs = props => {
  const { pages, viewMode, trackingData, openQuickPreview, isCalendarView, forcedLimit, forcedLimitCount } = props;
  const { entity, entityType } = usePageContext();

  const dataSource = useMemo(() => {
    if (isCalendarView) {
      return null;
    }

    if (viewMode === VIEW_MODES.DAY) {
      return groupProductionsPerformances(pages, entity);
    }

    const cards = pages?.flatMap(({ data }) => data) || [];

    if (forcedLimit) {
      return cards.slice(0, forcedLimitCount);
    }

    return cards;
  }, [pages, entity, viewMode, isCalendarView, forcedLimit, forcedLimitCount]);

  if (isCalendarView) {
    return <CalendarProductionListing rows={<RenderSlugs {...props} isCalendarView={false} />} isProView hideFilters />;
  }

  if (!dataSource?.length) {
    return null;
  }

  const SlugComponent = viewMode === VIEW_MODES.CALENDAR ? CalendarViewSlug : ProductionPublicSlug;

  return (
    <>
      <JsonLD events={dataSource} />
      {dataSource?.map((production, index) => (
        <SlugComponent
          key={`${production.id}-${index}`}
          data={production}
          entity={entity}
          entityType={entityType}
          trackingData={trackingData}
          openQuickPreview={openQuickPreview}
          isDayView={viewMode === VIEW_MODES.DAY}
          upcoming={viewMode === VIEW_MODES.DAY}
          isFeaturedCard={viewMode === VIEW_MODES.GRID}
          isCondensedView={viewMode === VIEW_MODES.MINI}
        />
      ))}
    </>
  );
};

const ProductionFeed = ({ children, withHorizontalScroller = false }) => {
  if (withHorizontalScroller) {
    return <HorizontalScroller hideArrow>{children}</HorizontalScroller>;
  }

  return children;
};

const ProductionListFeed = ({
  viewMode,
  queryFn,
  infinite = true,
  limit = 12,
  trackingData,
  scrollable = false,
  styles,
  noResultProps,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { entity } = usePageContext();
  const { showQuickView } = useQuickView();

  const openQuickPreview = useCallback(
    (e, { productionId, scrollToSectionId, performanceId }) => {
      showQuickView({
        type: QUICK_VIEW_TYPES.PRODUCTION,
        data: {
          entityId: productionId,
          quickViewContextId: entity?.id,
          performanceId,
          scrollToSectionId,
        },
      });
    },
    // NOTE: showQuickView is not required as dependency for this hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity?.id],
  );

  return (
    <div
      className={classnames(classes.root, {
        [styles?.root]: !!styles?.root,
        [classes.calendar]: viewMode === VIEW_MODES.CALENDAR,
      })}
    >
      <InfiniteListPage query={queryFn} disabled={!infinite} disableQueryUpdate updateAppliedFilters={infinite}>
        {({ pages, count, isLoading }) => {
          if (!isLoading && count === 0) {
            return (
              <NoResult
                hideIcon={noResultProps?.hideIcon}
                title={noResultProps?.title || t(`${TP}.FN_ARTIST_PRO_SEARCH_NO_RESULT`)}
                subtitle={noResultProps?.subtitle || t(`${TP}.FN_ARTIST_PRO_SEARCH_NO_RESULT_SUBTITLE`)}
              />
            );
          }

          if (viewMode === VIEW_MODES.CALENDAR) {
            return (
              <>
                <RenderSlugs
                  pages={pages}
                  viewMode={viewMode}
                  trackingData={trackingData}
                  openQuickPreview={openQuickPreview}
                  isCalendarView
                />
                <PageLoading defaultLoader />
              </>
            );
          }

          return (
            <>
              <ProductionFeed withHorizontalScroller={scrollable && viewMode === VIEW_MODES.GRID}>
                <ul
                  className={classnames(classes.feedList, {
                    [classes.grid]: viewMode === VIEW_MODES.GRID,
                    [styles?.list]: !!styles?.list,
                  })}
                >
                  <RenderSlugs
                    pages={pages}
                    viewMode={viewMode}
                    trackingData={trackingData}
                    openQuickPreview={openQuickPreview}
                    forcedLimit={!infinite}
                    forcedLimitCount={limit}
                  />
                </ul>
              </ProductionFeed>
              <PageLoading defaultLoader />
            </>
          );
        }}
      </InfiniteListPage>
    </div>
  );
};

export default ProductionListFeed;
